import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, TrendingUp, PiggyBank, Sun, Construction, CalendarDays, FileText, Landmark, Megaphone, Newspaper, Bell, Camera, PlayCircle, Phone, Search, MapPin, Building2, Receipt, Users, Compass, Map, Vote, HandHeart, Signpost, Star, Heart, ListChecks } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { CvDownloadSection } from '../components/CvDownload';

// Anchor aliases so menu links like /resources#money keep working alongside
// the original section ids (#your-money etc.).
const GROUPS = [
  {
    id: 'election-and-ward',
    aliases: ['election', 'your-ward', 'ward'],
    kicker: 'THE 2026 ELECTION & YOUR WARD',
    title: 'Who is running, where you vote, and how',
    items: [
      { icon: Users, title: "Who's Running", body: 'Every candidate for Mayor, all eight wards and school trustee, from official City nomination data.', url: '/whos-running', label: 'See the Candidates' },
      { icon: Compass, title: 'Find Your Ward', body: 'Type your address and find out which ward you vote in and who your candidates are.', url: '/find-your-ward', label: 'Find My Ward' },
      { icon: Map, title: 'Ward Boundaries Map', body: 'The official Kawartha Lakes ward boundary map. This shows where each ward starts and ends. It is not the PITA issue map.', url: '/elections/ward-boundaries', label: 'Open Ward Boundaries' },
      { icon: Vote, title: 'How to Vote', body: 'Voting methods, ID, dates and what to expect, with links to the official City election pages.', url: '/elections/how-to-vote', label: 'Voting Guidance' },
      { icon: CalendarDays, title: 'Election Event List', body: 'Debates, forums and candidate events across Kawartha Lakes, with calendar downloads and reminders.', url: '/elections/events', label: 'See the Events' },
      { icon: ListChecks, title: 'Key Election Dates', body: 'Nomination, advance voting and election day dates from official sources.', url: '/elections/key-dates', label: 'Key Dates' },
    ],
  },
  {
    id: 'your-money',
    aliases: ['money'],
    kicker: 'YOUR MONEY',
    title: 'Where your tax dollars go',
    items: [
      { icon: BarChart3, title: 'City Data', body: 'Plain-language charts about City taxes and spending, with sources.', url: '/city-data', label: 'Explore City Data' },
      { icon: Receipt, title: 'City Finances, Explained', body: 'The budget, the levy, reserves, and debt in plain language with sources.', url: '/city-finances', label: 'See the Numbers' },
      { icon: TrendingUp, title: 'Tax Tracker', body: 'Follow property tax changes over time.', url: '/tax-tracker', label: 'Open Tax Tracker' },
      { icon: PiggyBank, title: 'Savings Tracker', body: 'Where taxpayers may not be getting full value, tracked in the open.', url: '/savings-tracker', label: 'View Savings Tracker' },
      { icon: Sun, title: 'Sunshine List', body: 'Public sector salary disclosure for Kawartha Lakes, explained simply.', url: '/sunshine-list', label: 'Open the Dashboard' },
      { icon: Construction, title: 'Infrastructure Tracker', body: 'Roads, bridges, and infrastructure spending at a glance.', url: '/infrastructure', label: 'View Infrastructure' },
    ],
  },
  {
    id: 'your-council',
    aliases: ['council'],
    kicker: 'YOUR COUNCIL',
    title: 'What Council is doing',
    items: [
      { icon: CalendarDays, title: 'Council Meetings', body: 'Upcoming and past meetings with agendas and plain-language notes.', url: '/meetings', label: 'See Meetings' },
      { icon: FileText, title: 'Accountability Reports', body: 'Ward 8 issue statistics and public accountability reporting.', url: '/reports', label: 'Read Reports' },
      { icon: Landmark, title: 'City Hall Watch', body: 'Keeping an eye on decisions that affect residents.', url: '/city-hall-watch', label: 'Open City Hall Watch' },
      { icon: Building2, title: 'CAO Review', body: 'The position, the structure, the cost, and the results: are taxpayers getting value?', url: '/cao-review', label: 'Read the Review' },
      { icon: Megaphone, title: 'How to Have Your Say', body: 'Practical steps to speak up at the City and be heard.', url: '/have-your-say', label: 'Have Your Say' },
      { icon: Landmark, title: 'Ward 8 Information', body: 'Communities, services and what is happening in Ward 8.', url: '/ward-8', label: 'Ward 8' },
    ],
  },
  {
    id: 'news-media',
    aliases: ['news'],
    kicker: 'NEWS & MEDIA',
    title: 'News, alerts, and media',
    items: [
      { icon: Newspaper, title: 'Newsroom', body: 'Campaign and community stories from the Kawartha People\'s Desk.', url: '/newsroom', label: 'Visit the Newsroom' },
      { icon: Bell, title: 'Public Alerts', body: 'Important public notices for Ward 8 residents.', url: '/alerts', label: 'See Alerts' },
      { icon: Camera, title: 'Photos', body: 'Beautiful Ward 8, campaign moments and the proof archive.', url: '/gallery', label: 'View Photos' },
      { icon: PlayCircle, title: "Steve's Videos", body: 'Two Minutes with Steve, event recordings and more.', url: '/videos', label: 'Watch Videos' },
      { icon: Megaphone, title: "What's Happening", body: 'Upcoming meetings, events and news in one feed.', url: '/whats-happening', label: 'See Everything' },
    ],
  },
  {
    id: 'practical-help',
    aliases: ['help'],
    kicker: 'PRACTICAL HELP',
    title: 'Get help with a City problem',
    items: [
      { icon: Phone, title: 'Who Do I Call?', body: 'The right City contact for common problems, in one directory.', url: '/who-do-i-call', label: 'Find the Contact' },
      { icon: Search, title: 'Track My Issue', body: 'Enter your W8 tracking number and follow your report.', url: '/issue-tracker/track', label: 'Track an Issue' },
      { icon: MapPin, title: 'PITA Public Issue Map', body: 'Reported Ward 8 issues, pinned on a map. This is the issue map, not the ward boundary map.', url: '/map', label: 'Open the Issue Map' },
    ],
  },
  {
    id: 'get-involved',
    aliases: ['volunteer', 'participate'],
    kicker: 'GET INVOLVED',
    title: 'Ways to take part',
    items: [
      { icon: HandHeart, title: 'Volunteer', body: 'Canvassing, sign delivery, events, or an hour a week from home.', url: '/volunteer', label: 'Volunteer' },
      { icon: Signpost, title: 'Request a Sign', body: 'A free lawn sign, placed and collected by a volunteer.', url: '/request-a-sign', label: 'Request a Sign' },
      { icon: Heart, title: 'Ways to Help', body: 'Every way to support the campaign, big or small.', url: '/get-involved', label: 'See All the Ways' },
      { icon: Star, title: 'Leave a Review', body: 'Tell other residents about your experience with Steve.', url: '/leave-a-review', label: 'Leave a Review' },
      { icon: Users, title: 'Supporters', body: 'Residents who have added their name in support.', url: '/supporters', label: 'See Supporters' },
      { icon: MapPin, title: 'Support PITA', body: 'Help keep the Public Issue Tracker running for Ward 8.', url: '/support-pita', label: 'Support PITA' },
    ],
  },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHeader
        pageKey="resources"
        kicker="RESOURCES"
        title="Facts, reports, and tools"
        subtitle="Everything in one place: the 2026 election and your ward, City data, council information, news, practical help and ways to get involved."
      />
      <RichSection id="resources-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* Two maps, two jobs: make the difference unmistakable */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="resources-map-distinction">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-[#c7d7f5] bg-[#f3f7ff]"><CardContent className="p-5 flex items-start gap-4">
            <span className="h-11 w-11 rounded-xl bg-[#1a2744] flex items-center justify-center shrink-0"><Map className="h-5 w-5 text-[#C9A84C]" /></span>
            <div className="min-w-0">
              <p className="font-serif font-bold text-[#1a2744]">Ward Boundaries Map</p>
              <p className="text-sm text-slate-600 mt-1">Where each of the eight wards starts and ends, from the City's official boundary data. Use it to check which ward you vote in.</p>
              <Link to="/elections/ward-boundaries" className="inline-flex items-center gap-1 mt-2 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="resources-ward-boundaries-link">Open Ward Boundaries <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </CardContent></Card>
          <Card className="border-red-200 bg-red-50/60"><CardContent className="p-5 flex items-start gap-4">
            <span className="h-11 w-11 rounded-xl bg-[#b91c1c] flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-white" /></span>
            <div className="min-w-0">
              <p className="font-serif font-bold text-[#1a2744]">PITA Issue Map</p>
              <p className="text-sm text-slate-600 mt-1">Ward 8 problems residents have reported through PITA, pinned where they are (approximate, privacy-safe). Use it to see and track issues.</p>
              <Link to="/map" className="inline-flex items-center gap-1 mt-2 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="resources-pita-map-link">Open the Issue Map <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </CardContent></Card>
        </div>
      </section>

      {GROUPS.map((g, gi) => (
        <section key={g.id} id={g.id} className={gi % 2 === 1 ? 'bg-white border-y border-slate-200' : ''} data-testid={`resources-group-${g.id}`}>
          {(g.aliases || []).map((a) => <span key={a} id={a} aria-hidden="true" />)}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
            <SectionHeader kicker={g.kicker} title={g.title} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {g.items.map(({ icon: Icon, title, body, url, label }) => (
                <Card key={title} className="card-lift" data-testid="resources-card">
                  <CardContent className="p-5 flex flex-col h-full">
                    <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                      <Icon className="h-5 w-5 text-[#C9A84C]" />
                    </div>
                    <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed flex-1">{body}</p>
                    <Link to={url} className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid={`resources-link-${url.replace(/\//g, '')}`}>{label} <ArrowRight className="h-3.5 w-3.5" /></Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Downloads */}
      <section id="downloads" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 space-y-4" data-testid="resources-downloads-section">
        <SectionHeader kicker="DOWNLOADS" title="Take Steve's information with you" />
        <FlyerDownloadSection loc="resources" />
        <CvDownloadSection loc="resources" />
      </section>

      {/* PITA cross-link */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-[#1a2744] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="resources-pita-band">
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-1">GOT AN ISSUE? PUT IT IN PITA!</p>
            <p className="font-serif font-black text-white text-xl leading-snug">Report It. Map It. Track It. Get Answers.</p>
          </div>
          <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press shrink-0" data-testid="resources-pita-button">
            <Link to="/issue-tracker/report">Report an Issue</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
